import React, { useCallback, useEffect, useRef } from 'react';
import { useState } from 'react';
import PaymentStep from '../../../components/ui/PaymentStep';
import { RadioGroup, Radio } from '@nextui-org/react';
import MyInput from '../../../components/ui/MyInput';
import CreditCard from '../../../components/ui/CreditCard';
import PaymentInfo from '../../../components/ui/PaymentInfo';
import { Dropdown } from 'primereact/dropdown';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import PrimaryButton from '../../../components/ui/PrimaryButton';
import { addSpacesToCreditCard, addSpacesToCreditCardDate, validateCard } from '../../../helper/Validators';
import CreateAction from '../../../components/operational/createAction';
import { localeStringConverter, toLocaleStringConverter } from '../../../helper/Converters';
import systemSlice from '../../../redux/systemSlice';
import { useDispatch } from 'react-redux';
import PaymentInstallements from './PaymentInstallements';
import { debounce } from '../../../helper/Helpers';

const defaultPaymentData = {
  "currencySymbol": "₺",
  "billingPeriod": "",
  "nextBillingDate": "",
  "licensePrice": "",
  "discountForUnusedDays": "",
  "licenseWorth": "",
  "discountCode": "",
  "discountType": "",
  "IsdiscountValid": "",
  "discountPrice": "",
  "surplusAmount": "",
  "tax": "",
  "taxAmount": "",
  "finalizedPaymentAmount": "",
  "requestLicenseType": "",
  "requestLicenseStartDate": "",
  "requestLicenseEndDate": "",
  "isFree": false
}

const Payment = ({ updateBillingAddress, billingAdress, license, callback }) => {
  const [installments, setInstallments] = useState({})
  const dispatch = useDispatch()
  const [couponCodeOpen, setCouponCodeOpen] = useState(false)
  const [couponCode, setCouponCode] = useState("")
  const [activeCouponCode, setActiveCouponCode] = useState("")



  const [cardInfo, setCardInfo] = useState({
    number: "",
    name: "",
    cvc: "",
    date: ""
  })
  const [binNumber, setBinNumber] = useState("")
  const getInstallments =
    async (bin) => {
      if (bin?.length < 8) return
      setInstallments({})
      const response = await CreateAction({
        method: 'post',
        path: "/installments",
        showPreLoader: false,
        hideToastMessages: true,
        isFileUploading: false,
        data: {
          licenseType: license.licenseType,
          discountCode: activeCouponCode,
          binNumber: bin
        }
      });

      if (response.error) return;
      const { installmentRates, binNumber } = response.result.resultData;
      if (!installmentRates || !binNumber) return;
      setInstallments({ [`${binNumber}_${activeCouponCode}`]: installmentRates });
    }


  const delayedGetInstallments = useCallback(
    debounce(async (bin) => getInstallments(bin), 50),
    [activeCouponCode] // Only create the debounced function once
  );




  const [installmentPeriod, setInstallmentPeriod] = useState(1)

  const [paymentInfo, setPaymentInfo] = useState(defaultPaymentData)

  const pay = async (e) => {
    e.preventDefault()

    const dateSplitted = cardInfo.date?.split("/")
    const requestReadyCardInfo = {
      CardHolderName: cardInfo.name,
      No: cardInfo?.number?.replaceAll(" ", ""),
      SkuMonth: dateSplitted[0],
      SkuYear: dateSplitted[1],
      CVC: cardInfo.cvc
    }
    const isValidCard = validateCard(requestReadyCardInfo.No)
    if (!isValidCard.isValid) return
    const response = await CreateAction({
      method: 'post',
      path: `/createPayment`,
      showPreLoader: true,
      isFileUploading: false,
      data: {
        binNumber: binNumber,
        licenseType: license.licenseType,
        discountCode: activeCouponCode,
        cc: requestReadyCardInfo,
        installmentPeriod
      }
    });
    if (response?.error || !response.result?.resultData) return

    setTimeout(() => {
      dispatch(systemSlice.actions.setIframeProps({ htmlContent: response.result?.resultData?.UCD_HTML }))
    }, 250)

  }

  const getPricing = async (syncCouponCode) => {

    const binNumber = String(cardInfo.number)?.replaceAll(" ", "")?.substring(0, 8)
    const response = await CreateAction({
      method: 'post',
      path: `/calculateUpgradeLicense`,
      showPreLoader: true,
      isFileUploading: false,
      hideSuccessToastMessages: true,
      data: {
        licenseType: license.licenseType,
        discountCode: activeCouponCode,
        binNumber: binNumber,
        installmentPeriod
      }
    });
    if (response.error) {
      if (response?.result?.data?.resultMessage?.includes("DISCOUNT_CODE"))
        setActiveCouponCode("")
      return
    }
    // if (response?.error || !response.result?.resultData) return (window.location.href = "/dashboard/theme-settings/license-details")

    setPaymentInfo(response.result?.resultData)
  }


  useEffect(() => {
    if (license?.id) {
      getPricing()

    }
  }, [license.id, activeCouponCode, installmentPeriod])


  useEffect(() => {
    getInstallments(binNumber)
  }, [activeCouponCode])

  useEffect(() => {
    delayedGetInstallments(binNumber)

  }, [binNumber])

  useEffect(() => {
    const binNumber = String(cardInfo.number)?.replaceAll(" ", "")?.substring(0, 8);

    setBinNumber(binNumber)
  }, [cardInfo?.number])

  const handleMessage = (event) => {
    if (event.data.type === 'mobligo-payment-url-change') {
      dispatch(systemSlice.actions.setIframeProps({}))
      if (event.data.isSuccess)
        window.location.href = "/dashboard/theme-settings/license-details"
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    // Cleanup when the component unmounts
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);


  const setCardInfoWithKey = (key, value) => {
    setCardInfo(state => ({ ...state, [key]: value }))
  }


  const keyCode = `${binNumber}_${activeCouponCode}`
  useEffect(() => {
    if (!installments[keyCode]) {
      setInstallmentPeriod(1)
    }
  }, [installments[keyCode]])

  return (
    <>
      <form className="flex flex-1 gap-[20px]  overflow-y-auto" onSubmit={pay}>
        <div className="flex flex-col gap-[20px] w-[100%]">
          {!paymentInfo?.isFree &&
            <>
              <div className={`bg-white border border-[#E3E8EF] rounded-[5px] duration-300 ${paymentInfo.finalizedPaymentAmount === 0 ? "opacity-50 select-none pointer-events-none	cursor-not-allowed" : "opacity-100"}`}>
                <div className="border-b border-[#E3E8EF] p-[20px]">Ödeme Seçenekleri</div>
                <div className="border border-[#E3E8EF] m-[20px]">
                  <div className="flex flex-col gap-[20px]">
                    <div className="flex items-center  gap-[10px] pl-[20px] pb-[20px] pt-[20px] border-b border-[#E3E8EF]">
                      <div className="flex items-center justify-center px-[10px] py-[5px] border border-[#E3E8EF] rounded">
                        <svg
                          fill="#000000"
                          width="20px"
                          height="20px"
                          viewBox="0 0 0.6 0.6"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M0.175 0.375H0.25a0.025 0.025 0 0 0 0 -0.05H0.175a0.025 0.025 0 0 0 0 0.05M0.475 0.125H0.125a0.075 0.075 0 0 0 -0.075 0.075v0.225A0.075 0.075 0 0 0 0.125 0.5h0.35A0.075 0.075 0 0 0 0.55 0.425V0.2A0.075 0.075 0 0 0 0.475 0.125M0.5 0.425a0.025 0.025 0 0 1 -0.025 0.025H0.125A0.025 0.025 0 0 1 0.1 0.425V0.275H0.5Zm0 -0.2H0.1V0.2A0.025 0.025 0 0 1 0.125 0.175h0.35A0.025 0.025 0 0 1 0.5 0.2Z" />
                        </svg>
                      </div>
                      <div className="flex items-center justify-center">Kredi Kartı / Banka Kartı</div>
                    </div>
                    <div className="flex flex-col gap-[20px] p-[20px]" >
                      <div>
                        <span className="text-[#5E5873] block mb-[5px] text-[14px] font-medium">
                          Kart Numarası <span className="text-[#7059EF]">*</span>
                        </span>
                        <MyInput
                          value={cardInfo.number}

                          maxlength="19"
                          placeholder="xxxx xxxx xxxx xxxx"
                          onChangeEvent={event => {
                            setCardInfoWithKey("number", addSpacesToCreditCard(event.target))
                          }}

                          type="text"
                          isRequired={!paymentInfo?.isFree}
                        />
                      </div>
                      <div>
                        <span className="text-[#5E5873] block mb-[5px] text-[14px] font-medium">
                          Kart Üzerindeki Ad Soyad <span className="text-[#7059EF]">*</span>
                        </span>
                        <MyInput
                          value={cardInfo.name}
                          placeholder="Kart Üzerindeki Ad Soyad"
                          onChange={value => {
                            setCardInfoWithKey("name", value)
                          }}
                          isRequired={!paymentInfo?.isFree}
                        />
                      </div>
                      <div className="flex gap-[20px]">
                        <div className="w-[50%]">
                          <span className="text-[#5E5873] block mb-[5px] text-[14px] font-medium">
                            Bitiş Tarihi <span className="text-[#7059EF]">*</span>
                          </span>
                          <MyInput
                            value={cardInfo.date}
                            placeholder="MM/YY"
                            maxlength="5"
                            type="text"
                            onChangeEvent={event => {
                              setCardInfoWithKey("date", addSpacesToCreditCardDate(event.target))

                            }}
                            isRequired={!paymentInfo?.isFree}
                          />
                        </div>
                        <div className="w-[50%]">
                          <span className="text-[#5E5873] block mb-[5px] text-[14px] font-medium">
                            Güvenlik Kodu <span className="text-[#7059EF]">*</span>
                          </span>
                          <MyInput
                            value={cardInfo.cvc}
                            maxLength="3"
                            placeholder="CVC"
                            onChange={value => {
                              setCardInfoWithKey("cvc", value)

                            }}
                            isRequired={!paymentInfo?.isFree}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {installments && binNumber?.length >= 8 && installments[keyCode]?.length > 0 ? <PaymentInstallements installments={installments[keyCode]} setInstallmentPeriod={setInstallmentPeriod} installmentPeriod={installmentPeriod} /> : <></>}

            </>
          }
          <div className="bg-white border border-[#E3E8EF] rounded-[5px]">
            <div className="flex justify-between items-center border-b border-[#E3E8EF] p-[20px]">
              <div>Fatura Adresi</div>
              <div className="flex gap-[10px] items-center text-[#7455ff] cursor-pointer"
                onClick={updateBillingAddress}
              >
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="18" viewBox="0 0 15 18" fill="none">
                    <path d="M1 17H13.8" stroke="#7455ff" stroke-width="1.5" stroke-linecap="round" />
                    <path
                      d="M8.91044 2.33034L9.50364 1.73714C10.4865 0.754287 12.08 0.754287 13.0629 1.73714C14.0457 2.71999 14.0457 4.3135 13.0629 5.29636L12.4697 5.88956M8.91044 2.33034C8.91044 2.33034 8.98459 3.5909 10.0968 4.70315C11.2091 5.81541 12.4697 5.88956 12.4697 5.88956M8.91044 2.33034L3.45685 7.78393C3.08746 8.15332 2.90277 8.33801 2.74393 8.54165C2.55657 8.78188 2.39593 9.0418 2.26486 9.31682C2.15375 9.54995 2.07115 9.79774 1.90596 10.2933L1.20596 12.3933M12.4697 5.88956L7.01606 11.3432C6.64668 11.7125 6.46199 11.8972 6.25835 12.0561C6.01812 12.2434 5.7582 12.4041 5.48318 12.5351C5.25005 12.6462 5.00225 12.7288 4.50668 12.894L2.40667 13.594M2.40667 13.594L1.89334 13.7652C1.64946 13.8464 1.38058 13.783 1.1988 13.6012C1.01703 13.4194 0.953554 13.1505 1.03485 12.9067L1.20596 12.3933M2.40667 13.594L1.20596 12.3933"
                      stroke="#7455ff"
                      stroke-width="1.5"
                    />
                  </svg>
                </div>
                <div>Düzenle</div>
              </div>
            </div>
            <div className="flex flex-col p-[20px] gap-[5px]">
              <div className="text-[#000000]">{billingAdress.name} {billingAdress.surname}</div>
              <div className="text-[#4a5567]">{billingAdress.adress}</div>
              <div className="text-[#4a5567]">{billingAdress.adress2}</div>
              <div className="text-[#4a5567]">{billingAdress.city} {billingAdress.county}</div>
              <div className="text-[#4a5567]">{billingAdress.country}</div>
            </div>
          </div>
        </div>
        <div className="bg-white w-[75%] p-[20px] border border-[#E3E8EF] rounded-[5px] h-fit	">
          <div className="bg-white border border-[#E3E8EF] rounded-[5px]">
            <div className="border-b border-[#E3E8EF] p-[20px] bg-[#f9fafc]">
              {license?.title} <span className="text-[#9BA3AF]">/ Yıllık</span>
            </div>
            <div className=" m-[20px]">
              <div className="flex flex-col gap-[20px] ">
                <div className="flex justify-between">
                  <div>{license.title}</div>
                  <div>{paymentInfo.currencySymbol} {toLocaleStringConverter(paymentInfo.licensePrice, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} </div>
                </div>
                {paymentInfo.discountForUnusedDays > 0 &&
                  <div className="flex justify-between">
                    <div>Önceki Planınızdan İndirim</div>
                    <div className={"text-red-500"}>- {paymentInfo.currencySymbol} {toLocaleStringConverter(paymentInfo.discountForUnusedDays, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}  </div>
                  </div>

                }

                <div className="border border-[#E3E8EF]" />

                {paymentInfo?.IsdiscountValid ?
                  <>
                    <div className="flex gap-[5px] items-center justify-between items-center" >
                      <div className='flex gap-[5px] items-center' >

                        <svg className='cursor-pointer'
                          onClick={() => {
                            setCouponCode("");
                            setActiveCouponCode("")
                          }}
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" stroke='#7059ef' fill-rule="evenodd" clip-rule="evenodd"><path d="M11.5 0c6.347 0 11.5 5.153 11.5 11.5s-5.153 11.5-11.5 11.5-11.5-5.153-11.5-11.5 5.153-11.5 11.5-11.5zm0 1c5.795 0 10.5 4.705 10.5 10.5s-4.705 10.5-10.5 10.5-10.5-4.705-10.5-10.5 4.705-10.5 10.5-10.5zm-6.5 10h13v1h-13v-1z" /></svg>
                        <div className="flex items-center text-[#7455ff] text-[15px]">{paymentInfo.discountCode}</div>
                      </div>
                      <div className='flex gap-[5px] items-center'>
                        <div className="flex items-center text-[#7455ff] text-[15px]">- {paymentInfo.currencySymbol} {toLocaleStringConverter(paymentInfo.discountPrice, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>

                      </div>

                    </div>
                  </> :
                  <>
                    <div className="flex gap-[5px] items-center  cursor-pointer" onClick={() => setCouponCodeOpen(state => !state)}>
                      <svg
                        fill="#7455ff"
                        width="16px"
                        height="16px"
                        viewBox="0 0 25.6 25.6"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M12.8 0C5.731 0 0 5.731 0 12.8c0 7.07 5.731 12.8 12.8 12.8 7.07 0 12.8 -5.73 12.8 -12.8C25.6 5.731 19.87 0 12.8 0m0 24.025c-6.176 0 -11.2 -5.05 -11.2 -11.225 0 -6.176 5.024 -11.2 11.2 -11.2s11.2 5.024 11.2 11.2 -5.024 11.225 -11.2 11.225M18.4 12H13.6V7.2c0 -0.442 -0.358 -0.8 -0.8 -0.8s-0.8 0.358 -0.8 0.8v4.8H7.2c-0.442 0 -0.8 0.358 -0.8 0.8s0.358 0.8 0.8 0.8h4.8v4.8c0 0.442 0.358 0.8 0.8 0.8s0.8 -0.358 0.8 -0.8V13.6h4.8c0.442 0 0.8 -0.358 0.8 -0.8s-0.358 -0.8 -0.8 -0.8" />
                      </svg>
                      <div className="flex items-center text-[#7455ff] text-[14px]">İndirim kodu ekle</div>
                    </div>
                    {couponCodeOpen &&

                      <div className="flex flex-row border-[#7059EF] border-[2px] rounded-md overflow-hidden" >
                        <MyInput onKeyDown={(event) => {
                          if (event.keyCode === 13) { // 13, "Enter" tuşunun keyCode değeridir.
                            event.preventDefault(); // Varsayılan davranışı engeller (örneğin, formun gönderilmesini).
                            setActiveCouponCode(couponCode); // Çağırmak istediğiniz fonksiyon.
                          }
                        }} value={couponCode} onChange={(e) => setCouponCode(e)} classes="!border-0 !border-transparent !rounded-[0px]" placeholder='Kupon Kodu'></MyInput>
                        <PrimaryButton classes=' w-full flex-1 !h-[45px] !rounded-[0px]' value={"Uygula"} onClick={() => setActiveCouponCode(couponCode)}></PrimaryButton>

                      </div>
                    }
                  </>
                }


                <div className="border border-[#E3E8EF]" />
                <div className="flex justify-between">
                  <div>Ara Toplam</div>
                  <div>{paymentInfo.currencySymbol} {toLocaleStringConverter(paymentInfo.surplusAmount, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}   </div>
                </div>
                <div className="flex justify-between">
                  <div>Vergi (%{paymentInfo.taxAmount})</div>
                  <div>{paymentInfo.currencySymbol} {toLocaleStringConverter(paymentInfo.tax, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} </div>
                </div>
                {paymentInfo?.interestRate > 0 &&
                  < div className="flex justify-between">
                    <div>Vade Farkı</div>
                    <div>{paymentInfo.currencySymbol} {toLocaleStringConverter(paymentInfo.interestRate, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} </div>
                  </div>
                }



                <div className="border border-[#E3E8EF]" />

                <div className="flex items-center justify-between">
                  <div>
                    <div>Toplam</div>
                    <div className="text-[#667588] text-[14px]">Bir Sonraki Ödeme: {localeStringConverter(paymentInfo.nextBillingDate, true)}</div>
                  </div>
                  <div>{paymentInfo.currencySymbol}  {toLocaleStringConverter(paymentInfo.finalizedPaymentAmount, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="my-[20px]">
            <PrimaryButton isSubmitButton={true} value={'Ödemeyi Yap'} classes="w-[100%] py-[15px]" />
          </div>
          <div className="flex items-center justify-center gap-[5px] ">
            <svg width="20px" height="20px" viewBox="0 0 0.6 0.6" xmlns="http://www.w3.org/2000/svg" fill="none">
              <path
                fill="#667588"
                fill-rule="evenodd"
                d="M0.075 0.3a0.075 0.075 0 0 1 0.075 -0.075h0.3a0.075 0.075 0 0 1 0.075 0.075v0.175a0.075 0.075 0 0 1 -0.075 0.075H0.15a0.075 0.075 0 0 1 -0.075 -0.075zm0.25 0.05a0.025 0.025 0 1 0 -0.05 0v0.075a0.025 0.025 0 1 0 0.05 0z"
                clip-rule="evenodd"
              />
              <path
                stroke="#667588"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="0.05"
                d="M0.2 0.25V0.175a0.1 0.1 0 0 1 0.1 -0.1v0a0.1 0.1 0 0 1 0.1 0.1v0.075"
              />
            </svg>
            <div className="text-[#667588] text-[15px]">İşleminiz SSL şifrelemesi ile güvence altındadır.</div>
          </div>
        </div>

      </form >
    </>
  );
};

export default Payment;
